import React, {useEffect, useRef, useState} from 'react';
import axios from "../../../../components/plugins/axios";
import {Line} from 'react-chartjs-2';
import LiveCircle from '../../../../components/liveCircle/LiveCircle'
import {convertNumber} from '../../../../util/DominantUtil';
import {LIVE_BASE_URL} from '../../../../constants';
import ChartLoaderLive from "../../../../components/loaders/ChartLoaderLive";
import moment from "jalali-moment";
import {useIranStockMarket} from "../../../../components/store/IranStockMarket";

const RialSymbolImage = "/images/RiyalSymbol-small-white.png";

const wsAddress = LIVE_BASE_URL + 'individuals-cap-index';
// const wsAddress = 'ws://localhost:5837/individuals-cap-index';

const UPPER_BOUND = 180

export default function IndividualsCapIndexChart() {
    const chartComponent = useRef(null);
    const [isLoading, setLoading] = useState(true)
    const [timeData, setTimeData] = useState([]);
    const [buyData, setBuyData] = useState([]);
    const [sellData, setSellData] = useState([]);
    const [sumOfAll, setSumOfAll] = useState(0);
    const [intervalTime, setIntervalTime] = useState(null);
    const [getLiveData, resetLiveData] = useState(true);
    const isMarketTime = useIranStockMarket(state => state.isMarketTime)
    const [activeButtons, setActiveButtons] = useState([isMarketTime, true, false, false]);

    useEffect(() => {
        callApi(1, "1d")

        return () => {
            const tooltipBox = document.getElementById('chartjs-tooltip')
            if (tooltipBox) document.getElementById("chartjs-tooltip").remove()
        }
    }, [])

    const data = {
        labels: timeData,
        datasets: [
            {
                label: 'فروش',
                data: sellData,
                backgroundColor: 'rgba(239,83,80, 0.7)',
                borderColor: '#fff',
                borderWidth: 0.5,
                pointRadius: 0,
            },
            {
                label: 'خرید',
                data: buyData,
                backgroundColor: 'rgba(156,204,101, 0.7)',
                borderColor: '#fff',
                borderWidth: 0.5,
                pointRadius: 0,
            }
        ],
    };

    const options = {
        responsive: true,
        legend: {
            display: false
        },
        maintainAspectRatio: false,
        tooltips: {
            mode: "index",
            intersect: false,
            yAlign: 'bottom',
            enabled: false,
            position: "nearest",
            itemSort: function (a, b) {
                return b.datasetIndex - a.datasetIndex;
            },
            custom: function (tooltipModel) {
                // Tooltip Element
                var tooltipEl = document.getElementById('chartjs-tooltip');

                // Create element on first render
                if (!tooltipEl) {
                    tooltipEl = document.createElement('div');
                    tooltipEl.id = 'chartjs-tooltip';
                    tooltipEl.innerHTML = '<div class=".tooltip-box"></div>';
                    document.body.appendChild(tooltipEl);
                }

                // Hide if no tooltip
                if (tooltipModel.opacity === 0) {
                    tooltipEl.style.opacity = 0;
                    return;
                }

                // Set caret Position
                tooltipEl.classList.remove('above', 'below', 'no-transform');
                if (tooltipModel.yAlign) {
                    tooltipEl.classList.add(tooltipModel.yAlign);
                } else {
                    tooltipEl.classList.add('no-transform');
                }

                function getBody(bodyItem) {
                    return bodyItem.lines;
                }

                // Set Text
                if (tooltipModel.body) {

                    const titleLines = tooltipModel.title || [];
                    const bodyLines = tooltipModel.body.map(getBody);

                    let innerHtml = '';

                    titleLines.forEach(function (title) {
                        const time = moment(new Date(parseInt(title))).locale('fa').format("HH:mm:ss")
                        const day = moment(new Date(parseInt(title))).locale('fa').format("dddd DD MMMM") + ' - '
                        innerHtml += `<div class="tooltip-title"><span>${intervalTime === '1w' || intervalTime === '1mo' ? day : ''}</span><span> ${time}</span></div>`;
                    });

                    bodyLines.forEach(function (body, i) {
                        const dataset = body[0].split(":")
                        const label = dataset[0]
                        const lastData = parseInt(dataset[1].trim())
                        const colors = tooltipModel.labelColors[i];
                        const span = '<span class="colored-rectangle" style="' + 'background:' + colors.backgroundColor + '"></span>';
                        const title = '<span>' + label + '</span>'
                        const mappedDataSpan = '<span>' + convertNumber(lastData, 2) + '</span>'
                        const suffixSpan = '<img class="tooltip-currency-symbol" alt="IRR" src="' + RialSymbolImage + '" width="12" height="13" class="mr-1px mb-1px" />'
                        innerHtml += '<div><span class="tooltip-value">' + span + ' ' + title + " " + mappedDataSpan + suffixSpan + '</span></div>';
                    });

                    const tableRoot = tooltipEl.querySelector('div');
                    tableRoot.innerHTML = innerHtml;
                }

                // `this` will be the overall tooltip
                const position = this._chart.canvas.getBoundingClientRect();

                // Display, position, and set styles for font
                tooltipEl.style.opacity = 1;
                tooltipEl.style.position = 'absolute';
                tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
                tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
                tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
                tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px';
                tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
                tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px';
                tooltipEl.style.pointerEvents = 'none';
            },
        },
        scales: {
            yAxes: [{
                position: 'right',
                stacked: true,
                display: true,
                ticks: {
                    min: 0,
                    callback: function (value, index, values) {
                        return convertNumber(value, 1);
                    }
                },
            }],
            xAxes: [{
                beginAtZero: true,
                ticks: {
                    min: 0,
                    minRotation: 0,
                    maxRotation: 0,
                    maxTicksLimit: 3,
                    callback: function (t) {
                        const date = new Date(t)
                        if (intervalTime === "5s" || intervalTime === "1d") return moment(date).format("HH:mm:ss")
                        return moment(date).locale('fa').format("YYYY-MM-DD")
                    },
                },
                gridLines: {
                    color: "rgba(0, 0, 0, 0)",
                }
            }],
        },
        plugins: {
            threshold: false   // disable plugin 'p1' for this instance
        }
    }

    function callApi(index, interval) {
        setIntervalTime(interval)
        changeButtonClass(index)
        emptyChart()
        resetLiveData(false)

        axios
            .get(`/v1/query/market/charts/$IC?range=${interval}`).then((response) => {
            const d = response.data;

            setBuyData(d.buy)
            setSellData(d.sell)
            setTimeData(d.time)
            setSumOfAll(d.buy[0] + d.sell[0])

            chartComponent.current.chartInstance.update()

        }).catch((error) => {
            // console.log(error)
        });
    }

    function setLiveData(data) {
        if (getLiveData) {
            setIntervalTime('5s')
            const m = JSON.parse(data);
            if (timeData.length > UPPER_BOUND) {
                timeData.shift()
                buyData.shift()
                sellData.shift()
            }

            timeData.push(m.time);
            buyData.push(m.buy);
            sellData.push(m.sell);
            setSumOfAll(m.buy + m.sell)

            chartComponent.current.chartInstance.update()
        }
    }

    function emptyChart() {
        setTimeData([])
        setBuyData([])
        setSellData([])
    }

    function changeButtonClass(index) {
        const ab = activeButtons.map((v, k) => k === index ? true : false)
        setActiveButtons(ab)
    }

    return (
        <div>
            {timeData ?
                <div>
                    <div className="IndividualsCapIndexChart chart-top-menu row" style={{margin: '0px 43px 0px 26px'}}>
                        <span className="col-5 live-table-title">سرانه خرید/فروش حقیقی</span>
                        <div className="col-7 live-chart-ranges range-selectors" dir="rtl">
                            <span className={`date-range-btn ${activeButtons[3] ? 'active-button' : ''}`}
                                  onClick={() => {
                                      return callApi(3, "1mo")
                                  }}>1 ماه</span>
                            <span className={`date-range-btn ${activeButtons[2] ? 'active-button' : ''}`}
                                  onClick={() => {
                                      return callApi(2, "1w")
                                  }}>5 روز</span>
                            <span className={`date-range-btn ${activeButtons[1] ? 'active-button' : ''}`}
                                  onClick={() => {
                                      return callApi(1, "1d")
                                  }}>روز</span>
                            <span
                                className={`date-range-btn ${!isMarketTime ? 'disabled' : activeButtons[0] ? 'active-button' : ''}`}
                                onClick={() => {
                                    changeButtonClass(0)
                                    emptyChart()
                                    return resetLiveData(true)
                                }}>زنده  <LiveCircle style={{marginTop: '-2px'}}/></span>
                        </div>
                    </div>
                    <div className="just-ltr live-chart-height">
                        <Line
                            height={330}
                            width={'100%'}
                            data={data}
                            ref={chartComponent}
                            options={options}
                        />
                    </div>
                </div>
                :
                <ChartLoaderLive
                    style={{width: '100%'}}
                    width={610}
                    height={330}
                />
            }
        </div>
    );
}

